import { useSyncExternalStore } from "react";

/**
 * El tema, que es lo único que esta interfaz configura por sí sola.
 *
 * `tokens.css` ya deja el interruptor preparado: el tema por defecto es el del
 * sistema --oscuro, o claro si el sistema lo pide-- y las clases `.light` y
 * `.dark` mandan sobre él. Aquí solo se elige cuál de las tres cosas vale y se
 * recuerda en el navegador; ningún color vive en este archivo.
 *
 * No va al backend a propósito: es una preferencia de ESTE navegador y no un
 * dato del participante. El día que haya preferencias de usuario de verdad,
 * cambia de dónde sale `readTheme` y nada más.
 */
export const THEMES = ["system", "light", "dark"] as const;

export type Theme = (typeof THEMES)[number];

export const THEME_LABELS: Record<Theme, string> = {
  system: "Sistema",
  light: "Claro",
  dark: "Oscuro",
};

const STORAGE_KEY = "ccmas:theme";

const isTheme = (value: string | null): value is Theme =>
  value !== null && (THEMES as readonly string[]).includes(value);

/**
 * Lo que se eligió la última vez, o el sistema.
 *
 * El acceso va en `try`: en un navegador con el almacenamiento bloqueado
 * `localStorage` no devuelve vacío, lanza, y un ajuste no puede tumbar la
 * aplicación entera.
 */
export const readTheme = (): Theme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : "system";
  } catch {
    return "system";
  }
};

export const applyTheme = (theme: Theme): void => {
  const root = document.documentElement;
  root.classList.toggle("light", theme === "light");
  root.classList.toggle("dark", theme === "dark");

  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Si no se puede recordar, el tema vale para esta sesión y ya.
  }
};

/**
 * El tema que se ve DE VERDAD: el elegido, o el del sistema si se eligió
 * "Sistema".
 *
 * Lo necesita quien no lee los tokens --una librería que pone su propia clase
 * de tema--. Se mira el documento y no lo guardado, porque es el documento el
 * que manda en los colores.
 */
export const resolvedTheme = (): "light" | "dark" => {
  const root = document.documentElement.classList;
  if (root.contains("light")) return "light";
  if (root.contains("dark")) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
};

const subscribeToTheme = (notify: () => void): (() => void) => {
  const media = window.matchMedia("(prefers-color-scheme: light)");
  media.addEventListener("change", notify);
  // `applyTheme` cambia la clase del documento: eso también es cambiar de tema.
  const observer = new MutationObserver(notify);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  return () => {
    media.removeEventListener("change", notify);
    observer.disconnect();
  };
};

/** `resolvedTheme`, al día: cambia si cambia el ajuste o el del sistema. */
export const useResolvedTheme = (): "light" | "dark" =>
  useSyncExternalStore(subscribeToTheme, resolvedTheme);
